import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Layout from "@/components/Layout";
import Dashboard from "@/pages/Dashboard";
import Customers from "@/pages/Customers";
import Suppliers from "@/pages/Suppliers";
import Quotations from "@/pages/Quotations";
import Pipeline from "@/pages/Pipeline";
import Deals from "@/pages/Deals";
import Followups from "@/pages/Followups";
import Login from "@/pages/Login";
import Products from "@/pages/Products";
import Couriers from "@/pages/Couriers";
import Containers from "@/pages/Containers";
import UsersPage from "@/pages/Users";
import LoginActivity from "@/pages/LoginActivity";
import ScreenshotActivity from "@/pages/ScreenshotActivity";
import AuditTrail from "@/pages/AuditTrail";
import { AuthProvider, useAuth } from "@/lib/auth";
import { Toaster } from "sonner";

function Protected({ children }) {
  const { user, ready } = useAuth();
  if (!ready) return <div style={{ padding: 32, color: "var(--text-2)" }}>Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AdminOnly({ children }) {
  const { isAdmin } = useAuth();
  if (!isAdmin) return <Navigate to="/dashboard" replace />;
  return children;
}

// Blocks a sales user whose access for this resource is "hide" from reaching
// the page by direct URL (the nav item is already hidden for them). Admins and
// any non-hidden level pass through.
function RequireAccess({ accessKey, children }) {
  const { user, isAdmin } = useAuth();
  if (!isAdmin && user?.[accessKey] === "hide") return <Navigate to="/dashboard" replace />;
  return children;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route element={<Protected><Layout /></Protected>}>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/customers" element={<RequireAccess accessKey="customers_access"><Customers /></RequireAccess>} />
            <Route path="/suppliers" element={<RequireAccess accessKey="suppliers_access"><Suppliers /></RequireAccess>} />
            <Route path="/leads" element={<Deals />} />
            {/* Legacy path — the module was renamed from Deals to Leads. */}
            <Route path="/deals" element={<Navigate to="/leads" replace />} />
            <Route path="/pipeline" element={<Pipeline />} />
            <Route path="/followups" element={<Followups />} />
            <Route path="/quotations" element={<Quotations />} />
            <Route path="/products" element={<Products />} />
            <Route path="/couriers" element={<Couriers />} />
            <Route path="/containers" element={<Containers />} />
            <Route path="/users" element={<AdminOnly><UsersPage /></AdminOnly>} />
            <Route path="/activity" element={<AdminOnly><AuditTrail /></AdminOnly>} />
            <Route path="/login-activity" element={<AdminOnly><LoginActivity /></AdminOnly>} />
            <Route path="/screenshot-activity" element={<AdminOnly><ScreenshotActivity /></AdminOnly>} />
          </Route>
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
